import React, { Suspense, useEffect, useState } from 'react'
import Notifications from 'components/Notifications';
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { LinearProgress } from '@mui/material'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { MenuItem as MenuItemType, getNavigationConfig } from './navigationConfig'
import { Auth } from 'aws-amplify'
import Logo from './logo.png';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [loggedInUserName, setLoggedInUserName] = useState('');
  const location = useLocation();
  const [navConfig, setNavConfig] = useState<MenuItemType[]>([]);

  useEffect(() => {
    getNavigationConfig().then(setNavConfig);
  }, [location.pathname]);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(userInfo => {
        const email: string = userInfo.attributes.email;  // darragh.kirwan@manna.aero
        const emailUsername = email?.split('@')[0]; // darragh.kirwan
        const name = emailUsername?.split('.') // ['darragh', 'kirwan']
                      .map((namePiece: string) => namePiece.charAt(0).toUpperCase() + namePiece.slice(1)) // ['Darragh', 'Kirwan']
                      .join(' '); //  'Darragh Kirwan'

        setLoggedInUserName(name);
      })
  });

  return (
    <>
      <div>
        <Transition show={sidebarOpen}>
          <Dialog className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
            <TransitionChild
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div className="fixed inset-0 flex">
              <TransitionChild
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <TransitionChild
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon className="h-6 w-6 text-black" aria-hidden="true" />
                      </button>
                    </div>
                  </TransitionChild>
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center">
                      <img
                        className="max-h-6 bg-white mt-4"
                        src={Logo}
                        alt="Manna Logo"
                      />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        {navConfig.map(navigationSection => {
                          return (<li key={`mobile-navigationSection-${navigationSection.title}`}>
                            <div className="text-xs font-semibold leading-6 text-gray-400">{navigationSection.title}</div>
                            <ul role="list" className="-mx-2 mt-2 space-y-1">
                              {navigationSection.pages.map((page) => (
                                <li key={`mobile-page-${page.href}`}>
                                  <NavLink
                                    to={page.href}
                                    className={({ isActive }) => {
                                      return classNames(
                                        isActive
                                          ? 'bg-gray-50'
                                          : 'hover:bg-gray-50 hover:text-blue-600',
                                        'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700'
                                      )
                                    }}
                                  >
                                    {({ isActive }) => (
                                      <>
                                        <page.icon
                                          className="h-6 w-6 shrink-0 text-gray-600"
                                          aria-hidden="true"
                                        />
                                        <span className={`${isActive ? 'font-semibold' : 'font-normal'} truncate`}>{page.title}</span>
                                      </>
                                    )}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </li>)
                        })}
                      </ul>
                    </nav>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </Transition>

        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <img
                className="max-h-6 bg-white mt-4"
                src={Logo}
                alt="Manna logo"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                {navConfig.map(navigationSection => {
                  return (<li key={`desktop-nav-section-${navigationSection.title}`}>
                    <div className="text-xs font-semibold leading-6 text-gray-400">{navigationSection.title}</div>
                    <ul role="list" className="-mx-2 mt-2 space-y-1">
                      {navigationSection.pages.map((page) => (
                        <li key={`desktop-nav-page-${page.href}`}>
                          <NavLink
                            to={page.href}
                            className={({ isActive }) => {
                              return classNames(
                                isActive
                                  ? 'bg-gray-50'
                                  : 'hover:bg-gray-50 hover:text-blue-600',
                                'group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700'
                              )
                            }}
                          >
                            {({ isActive }) => (
                              <>
                                <page.icon
                                  className="h-6 w-6 shrink-0"
                                  aria-hidden="true"
                                />
                                <span className={`${isActive ? 'font-semibold' : 'font-normal'} truncate`}>{page.title}</span>
                              </>
                            )}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </li>);
                })}
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72">
          <div className="sticky top-0 z-40 lg:mx-auto">
            <div className="flex h-16 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6 text-black" aria-hidden="true" />
              </button>

              <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                <div className="relative flex flex-1"></div>
                <div className="flex items-center gap-x-4 lg:gap-x-6 mr-3">
                  <Menu as="div" className="relative">
                    <MenuButton className="-m-1.5 flex items-center p-1.5">
                      <span className="sr-only">Open user menu</span>
                      <span className="hidden lg:flex lg:items-center">
                        <span className="ml-4 text-sm font-semibold leading-6 text-black" aria-hidden="true">
                          {loggedInUserName}
                        </span>
                        <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </MenuButton>
                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              onClick={() => Auth.signOut()}
                              className={classNames(
                                focus ? 'bg-gray-50' : '',
                                'block px-3 py-1 text-sm leading-6 text-gray-900'
                              )}
                            >
                              Sign out
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>
          </div>

          <main>
            <div className="mx-auto">
              <Suspense fallback={<LinearProgress />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
          <Notifications />
        </div>
      </div>
    </>
  )
}

export default Dashboard;
